import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "@/api";
import { Comment } from "@/api/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faHistory,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { CommenterHeader } from "./CommentItem";
import RichTextDisplay from "@/components/widgets/richtext/RichTextDisplay";
import toast from "@/utils/toast";
import { motion } from "motion/react";
import { TimeAgo } from "@/components/widgets/TimeAgo";

interface CommentHistoryProps {
  commentId: string;
  onClose: () => void;
}

interface CommentHistoryEntry extends Comment {
  historyDate: string;
}

export default function CommentHistory({
  commentId,
  onClose,
}: CommentHistoryProps) {
  const [history, setHistory] = useState<CommentHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Fetch comment history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const historyData = await apiClient.get<CommentHistoryEntry[]>(
          `/comments/${commentId}/history`
        );
        setHistory(historyData);
      } catch (err: any) {
        console.error("Failed to fetch comment history:", err);
        setError("Failed to load comment history");
        toast.error("Failed to load comment history");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [commentId]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={faHistory}
              className="text-blue-600 mr-3 text-xl"
            />
            <h2 className="text-xl font-semibold text-gray-800">
              Comment History
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-blue-600 text-2xl animate-spin mr-3"
              />
              <span className="text-gray-600">Loading comment history...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-red-600 mb-2">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">
                No history available for this comment.
              </p>
            </div>
          ) : (
            <div className="p-6">
              <motion.div layout className="space-y-6">
                {history.map((entry, index) => {
                  const revIndex = (history.length - index) ; // Reverse index for versioning
                  return (
                    <div
                      key={`${entry.id}-v${revIndex}`}
                      className={`border-l-4 pl-4 ${
                        index === 0
                          ? "border-green-400 bg-green-50"
                          : "border-gray-300 bg-gray-50"
                      } rounded-r-lg p-3`}
                    >
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        {/* Version Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded ${
                                index === 0
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {index === 0
                                ? "Current Version"
                                : `Version ${revIndex}`}
                            </span>
                            {entry.isPublic ? (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                Public
                              </span>
                            ) : (
                              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                                Private
                              </span>
                            )}
                            {entry.isClarifyRequest && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                Clarification Request
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {index === 0 ? (
                              <TimeAgo
                                timestamp={entry.updatedAt || entry.createdAt}
                              />
                            ) : (
                              <TimeAgo timestamp={entry.historyDate} />
                            )}
                          </div>
                        </div>

                        {/* Author Information */}
                        <CommenterHeader comment={entry} />

                        {/* Comment Content */}
                        <div className="mt-3 text-gray-800">
                          <RichTextDisplay content={entry.content} />
                        </div>

                        {/* Metadata */}
                        {entry.totalVotes !== undefined && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span>Score: {entry.totalVotes}</span>
                              {entry.replies && entry.replies.length > 0 && (
                                <span>
                                  {entry.replies.length}{" "}
                                  {entry.replies.length === 1
                                    ? "reply"
                                    : "replies"}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
